import { BReportLabel, BTabs, BTabsContainer, SButton, SIconButton, SInputLabel, STitleContainer, STitleH3 } from './common/BCommonComponents';
import React, { useContext, useEffect, useState } from 'react';
import { Box, FormControl, Grid, MenuItem, Select, Tab } from '@material-ui/core';
import { AslDTO, DatoDTO, PuntoServizioDTO, ReportDataDTO, ReportFilterDTO, ReportPageDTO, TipoReportEnum, UserRole } from './common/Dtos';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FaCircle, FaSearch } from 'react-icons/all';
import { dateToIsoEndOfDay, dateToIsoStartOfDay, secondsToHms } from './common/DateUtils';
import { HorizontalGridLines, VerticalBarSeries, VerticalGridLines, XAxis, XYPlot, YAxis } from 'react-vis';
import { toast } from 'react-toastify';
import AuthContext from './auth/AuthContext';
import { DateTime } from 'luxon';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import LuxonUtils from '@date-io/luxon';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

export function Report() {
	const auth = useContext(AuthContext);
	const useStyles = makeStyles((theme: Theme) =>
		createStyles({
			formControl: {
				// margin: theme.spacing(1),
				width: '100%',
			},
			selectEmpty: {
				width: '100%',
				margin: '0',
			},
		}),
	);

	const classes = useStyles();
	const [aslList, setAslList] = useState<AslDTO[]>([]);
	const [puntiServizioList, setPuntiServizioList] = useState<PuntoServizioDTO[]>([]);
	const [errorDataInizio, setErrorDataInizio] = useState<boolean>(false);
	const [errorDataFine, setErrorDataFine] = useState<boolean>(false);
	const [maxDate, setMaxDate] = useState<string>('2100-12-31');
	const [minDate, setMinDate] = useState<string>('1970-01-01');
	const [reportPage, setReportPage] = useState<ReportPageDTO>({
		reportPrestazioniDTO: [],
		reportContattiDTO: []
	})
	const [filter, setFilter] = useState<ReportFilterDTO>({
		puntoServizioId: 'ALL',
		asl: 'ALL',
		dataInizio: '',
		dataFine: ''
	});
	const [tipoReport, setTipoReport] = useState<string>(TipoReportEnum.DEFAULT);
	const [value, setValue] = React.useState(0);

	useEffect(() => {
		getAllPuntiServizio();
		getAsl();
		getAll();
	}, []);

	const getAll = () => {
		const newFilter: ReportFilterDTO = {
			asl: filter.asl === 'ALL' ? '' : filter.asl,
			puntoServizioId: filter.puntoServizioId === 'ALL' ? '' : filter.puntoServizioId,
			dataInizio: filter.dataInizio !== '' ? dateToIsoStartOfDay(filter.dataInizio) : '',
			dataFine: filter.dataFine !== '' ? dateToIsoEndOfDay(filter.dataFine) : ''
		}

		auth.state.axiosInstance?.get('/api/report/recap', {params: newFilter})
			.then(res => {
				setReportPage(res.data)
			})
			.catch(error => {
				toast.error('🛑 Errore durante il recupero dei Punti di Servizio!');
				console.log(error);
			});
	}

	const getAllPuntiServizio = (asl?: string) => {
		if (asl) {
			auth.state.axiosInstance?.get('/api/puntoServizio/punti_servizio_of_asl/' + asl)
				.then(res => {
					setPuntiServizioList(res.data)
				})
				.catch(error => {
					toast.error('🛑 Errore durante il recupero dei Punti di Servizio!');
					console.log(error);
				});
		} else {
			if (auth.state.roles && (auth.state.roles.indexOf(UserRole.REPORT) >= 0)) {
				auth.state.axiosInstance?.get('/api/puntoServizio/punto_servizio_by_cooperativa?login=' + false)
					.then(res => {
						setPuntiServizioList(res.data)
					})
					.catch(error => {
						toast.error('🛑 Errore durante il recupero dei Punti di Servizio!');
						console.log(error);
					});
			} else {
				auth.state.axiosInstance?.get('/api/puntoServizio/punto_servizio_by_cooperativa/' + auth.state.cooperativa + '?login=' + false)
					.then(res => {
						setPuntiServizioList(res.data)
					})
					.catch(error => {
						toast.error('🛑 Errore durante il recupero dei Punti di Servizio!');
						console.log(error);
					});
			}
		}
	}

	const getAsl = () => {
		auth.state.axiosInstance?.get('/api/puntoServizio/asl/get_all')
			.then(res => {
				setAslList(res.data);
			})
			.catch(error => {
				toast.error('🛑 Errore durante il recupero della lista delle Asl!');
				console.log(error);
			})
	}

	const handleAslChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const newFilter: ReportFilterDTO = {
			...filter,
			asl: event.target.value as string
		}

		setFilter(newFilter);

		if (event.target.value === 'ALL') {
			getAllPuntiServizio();
		} else {
			getAllPuntiServizio(event.target.value as string);
		}
	}

	const handlePuntoServizioChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const newFilter: ReportFilterDTO = {
			...filter,
			puntoServizioId: event.target.value as string
		}

		setFilter(newFilter);
	}

	const handleTipoChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const newFilter: ReportFilterDTO = {
			...filter,
			dataInizio: '',
			dataFine: ''
		}

		setFilter(newFilter);
		setErrorDataFine(false);
		setErrorDataInizio(false);
		setMinDate('1970-01-01');
		setMaxDate('2100-12-31');
		setTipoReport(event.target.value as string);
	}

	const handleDataInizioChange = (date: MaterialUiPickersDate, value?: string | null) => {
		if (date && date.isValid) {
			const newFilter: ReportFilterDTO = {
				...filter,
				dataInizio: date ? date?.toString() : ''
			}
			setFilter(newFilter);

			let dateTime = DateTime.fromJSDate(new Date(newFilter.dataInizio)).toUTC();
			dateTime = dateTime.plus({days: 30});
			setMaxDate(dateTime.toISODate());
		}
	};

	const handleDataFineChange = (date: MaterialUiPickersDate, value?: string | null) => {
		if (date && date.isValid) {
			const newFilter: ReportFilterDTO = {
				...filter,
				dataFine: date ? date?.toString() : '',
			}
			setFilter(newFilter);

			let dateTime = DateTime.fromJSDate(new Date(newFilter.dataFine)).toUTC();
			dateTime = dateTime.plus({days: -30});
			setMinDate(dateTime.toISODate());
		}
	};

	const handleAzzeraFiltri = () => {
		setFilter({
			asl: 'ALL',
			puntoServizioId: 'ALL',
			dataInizio: '',
			dataFine: ''
		});
		setErrorDataFine(false);
		setErrorDataInizio(false);
		setTipoReport(TipoReportEnum.DEFAULT);
		getAllPuntiServizio();
	}

	const handleSearchClick = () => {
		if (tipoReport === TipoReportEnum.GIORNO && (filter.dataFine === '' || filter.dataInizio === '')) {
			setErrorDataInizio(filter.dataInizio === '');
			setErrorDataFine(filter.dataFine === '');
		} else if (tipoReport === TipoReportEnum.FASCE_ORARIE && filter.dataInizio === '') {
			setErrorDataInizio(true);
			setErrorDataFine(false);
		} else {
			setErrorDataFine(false);
			setErrorDataInizio(false);

			getAll();
		}
	}

	const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setValue(newValue);
	};

	return (
		<div>
			<STitleContainer>
				<STitleH3>Report</STitleH3>
			</STitleContainer>
			<Grid container spacing={0} className="w-100 mb-3">
				<Grid item xs={3} className="pr-2">
					<FormControl variant="outlined" className={classes.formControl}>
						<SInputLabel id="asl">ASL</SInputLabel>
						<Select
							labelId="asl"
							id="aslSelect"
							className={classes.selectEmpty}
							label="ASL"
							value={filter.asl}
							onChange={handleAslChange}
							MenuProps={{
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left"
								},
								transformOrigin: {
									vertical: "top",
									horizontal: "left"
								},
								getContentAnchorEl: null
							}}
						>
							<MenuItem value={"ALL"} key={"ALL"}>TUTTI</MenuItem>
							{aslList && aslList.map(asl => {
								return (
									<MenuItem value={asl.id} key={asl.id}>{asl.name}</MenuItem>
								)
							})}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={3} className="px-2">
					<FormControl variant="outlined" className={classes.formControl}>
						<SInputLabel id="asl">Punto di Servizio</SInputLabel>
						<Select
							labelId="punto"
							id="puntoSelect"
							className={classes.selectEmpty}
							label="Punto di Servizio"
							value={filter.puntoServizioId}
							onChange={handlePuntoServizioChange}
							MenuProps={{
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left"
								},
								transformOrigin: {
									vertical: "top",
									horizontal: "left"
								},
								getContentAnchorEl: null
							}}
						>
							{puntiServizioList && puntiServizioList.length > 0 &&
								<MenuItem value={"ALL"} key={"ALL"}>TUTTI</MenuItem>}
							{puntiServizioList && puntiServizioList.map(punto => {
								return (
									<MenuItem value={punto.id} key={punto.id}>{punto.name}</MenuItem>
								)
							})}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Grid container spacing={0} className="w-100">
				<Grid item xs={3} className="pr-2">
					<FormControl variant="outlined" className={classes.formControl}>
						<SInputLabel id="asl">Tipo</SInputLabel>
						<Select
							labelId="tipo"
							id="tipoSelect"
							className={classes.selectEmpty}
							label="Tipo"
							value={tipoReport}
							onChange={handleTipoChange}
							MenuProps={{
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left"
								},
								transformOrigin: {
									vertical: "top",
									horizontal: "left"
								},
								getContentAnchorEl: null
							}}
						>
							<MenuItem value={TipoReportEnum.DEFAULT} key={TipoReportEnum.DEFAULT}>Periodo Corrente</MenuItem>
							<MenuItem value={TipoReportEnum.FASCE_ORARIE} key={TipoReportEnum.FASCE_ORARIE}>Fasce orarie giorno</MenuItem>
							<MenuItem value={TipoReportEnum.GIORNO} key={TipoReportEnum.GIORNO}>Report giornaliero periodo</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				{tipoReport === TipoReportEnum.GIORNO &&
					<React.Fragment>
						<Grid item xs={3} className="px-2">
							<MuiPickersUtilsProvider utils={LuxonUtils} locale="it-IT">
								<KeyboardDatePicker
									id="dataInizio"
									label="Data Inizio"
									className="w-100"
									variant="inline"
									inputVariant="outlined"
									InputLabelProps={{shrink: true}}
									onChange={handleDataInizioChange}
									format="dd/MM/yyyy"
									okLabel={true}
									error={errorDataInizio}
									value={filter.dataInizio}
									minDate={minDate}
									required
								/>
							</MuiPickersUtilsProvider>
						</Grid>
						<Grid item xs={3} className="px-2">
							<MuiPickersUtilsProvider utils={LuxonUtils} locale="it-IT">
								<KeyboardDatePicker
									id="dataFine"
									label="Data Fine"
									className="w-100"
									variant="inline"
									inputVariant="outlined"
									InputLabelProps={{shrink: true}}
									onChange={handleDataFineChange}
									maxDate={maxDate}
									format="dd/MM/yyyy"
									okLabel={true}
									value={filter.dataFine}
									error={errorDataFine}
									required
								/>
							</MuiPickersUtilsProvider>
						</Grid>
					</React.Fragment>
				}
				{tipoReport === TipoReportEnum.FASCE_ORARIE &&
					<React.Fragment>
						<Grid item xs={3} className="px-2">
							<MuiPickersUtilsProvider utils={LuxonUtils} locale="it-IT">
								<KeyboardDatePicker
									id="dataFine"
									label="Data"
									className="w-100"
									variant="inline"
									inputVariant="outlined"
									InputLabelProps={{shrink: true}}
									onChange={handleDataInizioChange}
									format="dd/MM/yyyy"
									okLabel={true}
									error={errorDataInizio}
									value={filter.dataInizio}
									required
								/>
							</MuiPickersUtilsProvider>
						</Grid>
						<Grid item xs={3} className="px-2"></Grid>
					</React.Fragment>
				}
				{tipoReport !== TipoReportEnum.FASCE_ORARIE && tipoReport !== TipoReportEnum.GIORNO &&
					<Grid item xs={6} className="px-2"></Grid>
				}
				<Grid item xs={1} className="pl-2 d-flex align-items-center justify-content-center">
					<SIconButton onClick={handleSearchClick}><FaSearch className="mx-1"/></SIconButton>
				</Grid>
				<Grid item xs={2} className="px-2 d-flex align-items-center justify-content-center">
					<SButton variant="secondary" onClick={handleAzzeraFiltri}>Azzera Filtri</SButton>
				</Grid>
			</Grid>
			<BTabsContainer>
				<BTabs value={value} onChange={handleTabChange} aria-label="simple tabs example">
					<Tab label="Prestazioni" {...a11yProps(0)} />
					<Tab label="Contatti" {...a11yProps(1)} />
				</BTabs>
				<TabPanel reports={reportPage.reportPrestazioniDTO} value={value} index={0} isContatto={false}/>
				<TabPanel reports={reportPage.reportContattiDTO} value={value} index={1} isContatto={true}/>
			</BTabsContainer>
		</div>
	);
}

interface TabPanelProps {
	index: any;
	value: any;
	children?: React.ReactNode;
	reports: ReportDataDTO[];
	isContatto: boolean;
}

function TabPanel(props: TabPanelProps) {
	const {value, index, reports, isContatto} = props;

	const getDatiFO = (datiList: DatoDTO[]): any[] => {
		let ret: any[] = [];

		datiList.forEach(d => {
			const newDato = {x: d.label, y: d.numFO};
			ret.push(newDato);
		})

		return ret;
	}

	const getDatiBO = (datiList: DatoDTO[]): any[] => {
		let ret: any[] = [];

		datiList.forEach(d => {
			const newDato = {x: d.label, y: d.numBO};
			ret.push(newDato);
		})

		return ret;
	}

	const getDatiContatti = (datiList: DatoDTO[]): any[] => {
		let ret: any[] = [];

		datiList.forEach(d => {
			const newDato = {x: d.label, y: d.numContatti};
			ret.push(newDato);
		})

		return ret;
	}

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={'simple-tabpanel-${index}'}
		>
			{value === index &&
				<React.Fragment>
					{reports && reports.map(report => {
						return (
							<React.Fragment>
								<Grid container spacing={0} className="w-100 px-2 py-4">
									<Grid item xs={8} className="px-2 d-flex justify-content-center">
										<Box p={0}>
											<BReportLabel>{report.descrizione}</BReportLabel>
											{!isContatto &&
												<XYPlot margin={{bottom: 70}} xType="ordinal" width={750} height={300}>
													<VerticalGridLines/>
													<HorizontalGridLines/>
													<XAxis/>
													<YAxis/>
													<VerticalBarSeries
														data={getDatiFO(report.dati)}
														barWidth={0.5}
														fill={'#D8E4F8'}
														stroke={'#D8E4F8'}
													/>
													<VerticalBarSeries
														data={getDatiBO(report.dati)}
														barWidth={0.5}
														fill={'#002C74'}
														stroke={'#002C74'}
													/>
												</XYPlot>
											}
											{isContatto &&
												<XYPlot margin={{bottom: 70}} xType="ordinal" width={750} height={300}>
													<VerticalGridLines/>
													<HorizontalGridLines/>
													<XAxis/>
													<YAxis/>
													<VerticalBarSeries
														data={getDatiContatti(report.dati)}
														barWidth={0.5}
														fill={'#D8E4F8'}
														stroke={'#D8E4F8'}
													/>
												</XYPlot>
											}
										</Box>
									</Grid>
									<Grid item xs={4} className="px-4 pt-5">
										<Box p={0}>
											{!isContatto && <p className="d-flex align-items-center"><FaCircle className="color-blue-secondary" size={20}/><span className="ml-2">Prestazioni FrontOffice</span></p>}
											{!isContatto && <p className="d-flex align-items-center"><FaCircle className="color-primary" size={20}/><span className="ml-2">Prestazioni BackOffice</span></p>}
											{!isContatto && <p className=""><b>Totale prestazioni eseguite:</b> {report.totalePrestazioni}</p>}
											{!isContatto && <p className=""><b>Media prestazioni FrontOffice per singolo contatto:</b> {report.mediaPrestazioniContatto}</p>}
											{!isContatto && <p className=""><b>Tempo medio per prestazione FrontOffice:</b> {secondsToHms(report.tempoMedioFO)}</p>}
											{!isContatto && <p className=""><b>Tempo medio per prestazione BackOffice:</b> {secondsToHms(report.tempoMedioBO)}</p>}
											{isContatto && <p className="d-flex align-items-center"><FaCircle className="color-blue-secondary" size={20}/><span className="ml-2">Contatti</span></p>}
											{isContatto && <p className=""><b>Totale contatti eseguiti:</b> {report.totaleContatti}</p>}
											{isContatto && <p className=""><b>Tempo medio per contatto:</b> {secondsToHms(report.tempoMedioContatto)}</p>}
										</Box>
									</Grid>
								</Grid>
							</React.Fragment>
						);
					})}
				</React.Fragment>
			}
		</div>
	);
}

function a11yProps(index: any) {
	return {
		id: 'simple-tab-${index}',
		'aria-controls': 'simple-tabpanel-${index}',
	};
}

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		flexGrow: 1,
		backgroundColor: theme.palette.background.paper,
	},
}));
